import React from 'react';
import { motion } from 'framer-motion';
import AZ from '../../assets/logos/az.png';
import AZD from '../../assets/logos/AL.png';
import { Link } from 'react-router-dom';
import ServiceGridCarsx from './ServiceGridCarsx';

const colors = {
  bg: '#FFFFFF',
  gold: '#C69C6D',
  goldDark: '#B88A56',
  goldLight: '#F5E1C8',
  text: '#3B2F2A',
  textSoft: '#5C4A3D',
  cardBg: 'rgba(255, 255, 255, 0.75)',
  cardBorder: 'rgba(198, 156, 109, 0.3)',
  shadow: '0 8px 24px rgba(107, 90, 70, 0.06)',
  shadowHover: '0 16px 32px rgba(184, 138, 86, 0.2)',
};

const services = [
  {
    id: 1,
    image: 'images/services/service1.jpg',
    title: 'Lifestyle / Évasion',
    link: '/LifeStyleEvasion',
    texts: 'Azyekos vous invite à vivre une évasion unique, alliant raffinement, liberté, confort et art de vivre sur mesure.',
  },
  {
    id: 2,
    image: 'images/services/service2.jpg',
    title: 'Bien-Être',
    link: '/Bien-etre',
    texts: 'Azyekos cultive votre bien-être à travers des expériences luxueuses, apaisantes et personnalisées, pour un équilibre parfait du corps et de l’esprit.',
  },
  {
    id: 3,
    image: 'images/services/service3.jpg',
    title: 'Conciergerie Privée',
    link: '/Conciergerie-privee',
    texts: 'Notre Conciergerie Privée vous offre un accompagnement exclusif et sur mesure pour simplifier et sublimer votre quotidien.',
  },
  {
    id: 4,
    image: 'images/services/service4.jpg',
    title: 'Culture / Tourisme',
    link: '/Culture-Tourisme',
    texts: 'Avec Azyekos, découvrez la richesse culturelle et touristique autrement, à travers des expériences exclusives, raffinées et entièrement personnalisées.',
  },
  {
    id: 5,
    image: 'images/services/service5.jpg',
    title: 'Formalités Aéroportuaires',
    link: '/Formalites-aeroportuaire',
    texts: 'Nous prenons en charge toutes vos formalités aéroportuaires, de l’arrivée jusqu’au départ, pour un voyage fluide, serein et privilégié.',
  },
  {
    id: 6,
    image: 'images/services/service6.jpg',
    title: 'Airport Lounge',
    link: '/Lounges',
    texts: 'Profitez d’un Lounge VIP raffiné, combinant confort, élégance et espace de travail, pour optimiser vos instants avant chaque vol.',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

const ServiceGrid = () => {
  return (
    <>
      <style>{`
        .service-grid-section {
          background: ${colors.bg};
          padding: 80px 0;
          position: relative;
          overflow: hidden;
        }

        /* Légère texture dorée en arrière-plan */
        .service-grid-section::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 30% 20%, ${colors.goldLight}30, transparent 50%),
                      radial-gradient(circle at 70% 80%, ${colors.goldLight}25, transparent 50%);
          z-index: 0;
        }

        .container {
          position: relative;
          z-index: 1;
        }

        .section-title {
          font-size: clamp(2rem, 5vw, 2.8rem);
          font-weight: 700;
          color: ${colors.text};
          letter-spacing: -0.5px;
          margin-bottom: 0.5rem;
        }

        .title-underline {
          width: 80px;
          height: 3px;
          background: linear-gradient(90deg, ${colors.gold}, ${colors.goldDark});
          margin: 8px auto 0;
          border-radius: 2px;
        }

        /* Carte glassmorphism doré */
        .service-card {
          background: ${colors.cardBg};
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid ${colors.cardBorder};
          border-radius: 24px;
          box-shadow: ${colors.shadow};
          overflow: hidden;
          transition: all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
          cursor: pointer;
          display: flex;
          flex-direction: column;
        }

        .service-card:hover {
          transform: translateY(-6px);
          box-shadow: ${colors.shadowHover};
          border-color: ${colors.gold};
        }

        .service-card:hover .card-img-overlay {
          opacity: 0.25;
        }

        /* Image de la carte */
        .service-card-img {
          position: relative;
          width: 100%;
          height: 200px;
          overflow: hidden;
        }

        .service-card-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .service-card:hover .service-card-img img {
          transform: scale(1.05);
        }

        .card-img-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, ${colors.goldLight} 0%, ${colors.gold} 100%);
          opacity: 0;
          transition: opacity 0.4s ease;
          mix-blend-mode: overlay;
        }

        /* Contenu de la carte */
        .service-card-body {
          padding: 20px;
          display: flex;
          align-items: flex-start;
          gap: 15px;
          flex: 1;
        }

        .service-icon {
          width: 50px;
          height: 50px;
          object-fit: contain;
          filter: drop-shadow(0 4px 6px rgba(0,0,0,0.08));
          flex-shrink: 0;
        }

        .service-info h5 {
          font-size: 1.2rem;
          font-weight: 700;
          color: ${colors.text};
          margin-bottom: 0.25rem;
        }

        .service-info h5 a {
          color: inherit;
          text-decoration: none;
          transition: color 0.2s;
        }

        .service-info h5 a:hover {
          color: ${colors.gold};
        }

        .service-info p {
          font-size: 0.9rem;
          color: ${colors.textSoft};
          line-height: 1.5;
          margin-bottom: 0.5rem;
        }

        .more-link {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 0.85rem;
          font-weight: 600;
          color: ${colors.gold};
          text-decoration: none;
          transition: color 0.2s;
        }

        .more-link i {
          font-size: 0.9rem;
        }

        .more-link:hover {
          color: ${colors.goldDark};
        }

        @media (max-width: 768px) {
          .service-grid-section {
            padding: 60px 0;
          }
          .service-card-body {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
        }
      `}</style>

      <section className="service-grid-section">
        <div className="container">
          {/* Logo + titre */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-5"
          >
            <img src={AZ} alt="Azyekos Logo" className="img-fluid mb-3" style={{ maxWidth: 280 }} />
            <h2 className="section-title">Service d'Excellence</h2>
            <div className="title-underline" />
          </motion.div>

          {/* Grille de services */}
          <motion.div
            className="row"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {services.map((service) => (
              <motion.div
                key={service.id}
                className="col-12 col-md-6 col-lg-4 mb-4"
                variants={itemVariants}
              >
                <div className="service-card">
                  <div className="service-card-img">
                    <img src={service.image} alt={service.title} loading="lazy" />
                    <div className="card-img-overlay" />
                  </div>
                  <div className="service-card-body">
                    <img src={AZD} alt="icône" className="service-icon" />
                    <div className="service-info">
                      <h5>
                        <Link to={service.link}>{service.title}</Link>
                      </h5>
                      <p>{service.texts}</p>
                      <Link to={service.link} className="more-link">
                        <i className="fa fa-caret-right" /> Plus d'infos
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <ServiceGridCarsx />
    </>
  );
};

export default ServiceGrid;